import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AuditEvent {
  id?: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  error_message?: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

interface SecurityAuditConfig {
  enableAutoLogging?: boolean;
  logFailedAttempts?: boolean;
  logSuccessfulActions?: boolean;
  logSensitiveOperations?: boolean;
}

export function useSecurityAudit(config: SecurityAuditConfig = {}) {
  const [auditLogs, setAuditLogs] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const { profile } = useAuth();

  const {
    enableAutoLogging = true,
    logFailedAttempts = true,
    logSuccessfulActions = true,
    logSensitiveOperations = true
  } = config;

  const createAuditEvent = useCallback(async (
    action: string,
    resourceType: string,
    options: {
      resourceId?: string;
      success: boolean;
      errorMessage?: string;
      metadata?: Record<string, any>;
      skipLogging?: boolean;
    }
  ) => {
    if (!enableAutoLogging && !options.skipLogging) return;
    if (!logFailedAttempts && !options.success) return;
    if (!logSuccessfulActions && options.success) return;

    const event: AuditEvent = {
      user_id: profile?.user_id || 'anonymous',
      action,
      resource_type: resourceType,
      resource_id: options.resourceId,
      ip_address: await getClientIP(),
      user_agent: navigator.userAgent,
      success: options.success,
      error_message: options.errorMessage,
      metadata: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...options.metadata
      },
      timestamp: new Date().toISOString()
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Audit Event:', event);
    }

    // For admin actions, log to console for now (until types are updated)
    if (profile?.is_admin && logSensitiveOperations) {
      try {
        console.log('Security Audit Event:', event);
        // TODO: Enable database logging once admin_audit_log is in types
        // const { error } = await supabase
        //   .from('admin_audit_log')
        //   .insert([event]);
        
        // if (error) {
        //   console.error('Failed to log audit event:', error);
        // }
      } catch (error) {
        console.error('Audit logging error:', error);
      }
    }

    // Update local state
    setAuditLogs(prev => [...prev.slice(-99), event]); // Keep last 100 events

    return event;
  }, [profile, enableAutoLogging, logFailedAttempts, logSuccessfulActions, logSensitiveOperations]);

  const getClientIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unknown';
    } catch {
      return 'unknown';
    }
  };

  const logSpreadsheetAction = useCallback(async (
    action: 'SAVE' | 'LOAD' | 'DELETE' | 'EXPORT',
    spreadsheetId?: string,
    success: boolean = true,
    errorMessage?: string,
    metadata?: Record<string, any>
  ) => {
    return createAuditEvent(
      `SPREADSHEET_${action}`,
      'spreadsheet',
      {
        resourceId: spreadsheetId,
        success,
        errorMessage,
        metadata: {
          plan: profile?.plano,
          ...metadata
        }
      }
    );
  }, [createAuditEvent, profile]);

  const logAdminAction = useCallback(async (
    action: string,
    resourceType: string,
    resourceId?: string,
    success: boolean = true,
    errorMessage?: string,
    metadata?: Record<string, any>
  ) => {
    if (!profile?.is_admin) {
      console.warn('Non-admin user attempting to log admin action');
      return;
    }

    return createAuditEvent(
      `ADMIN_${action.toUpperCase()}`,
      resourceType,
      {
        resourceId,
        success,
        errorMessage,
        metadata: {
          adminLevel: 'super_admin',
          ...metadata
        }
      }
    );
  }, [createAuditEvent, profile]);

  const logAuthAction = useCallback(async (
    action: 'LOGIN' | 'LOGOUT' | 'REGISTER' | 'PASSWORD_RESET',
    success: boolean = true,
    errorMessage?: string,
    metadata?: Record<string, any>
  ) => {
    return createAuditEvent(
      `AUTH_${action}`,
      'authentication',
      {
        success,
        errorMessage,
        metadata: {
          method: 'email_password',
          ...metadata
        }
      }
    );
  }, [createAuditEvent]);

  const logDataAccess = useCallback(async (
    resourceType: string,
    resourceId: string,
    action: 'READ' | 'WRITE' | 'DELETE',
    success: boolean = true,
    errorMessage?: string,
    metadata?: Record<string, any>
  ) => {
    return createAuditEvent(
      `DATA_${action}`,
      resourceType,
      {
        resourceId,
        success,
        errorMessage,
        metadata: {
          sensitive: true,
          ...metadata
        }
      }
    );
  }, [createAuditEvent]);

  const fetchAuditLogs = useCallback(async (
    filters: {
      userId?: string;
      action?: string;
      resourceType?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
    } = {}
  ) => {
    if (!profile?.is_admin) {
      console.warn('Non-admin user attempting to fetch audit logs');
      return [];
    }

    setLoading(true);
    try {
      // TODO: Enable database querying once admin_audit_log is in types
      console.log('Fetching audit logs with filters:', filters);
      
      // For now, return local audit logs
      const filteredLogs = auditLogs.filter(log => {
        if (filters.userId && log.user_id !== filters.userId) return false;
        if (filters.action && !log.action.toLowerCase().includes(filters.action.toLowerCase())) return false;
        if (filters.resourceType && log.resource_type !== filters.resourceType) return false;
        if (filters.startDate && log.timestamp < filters.startDate) return false;
        if (filters.endDate && log.timestamp > filters.endDate) return false;
        return true;
      });

      const result = filters.limit ? filteredLogs.slice(0, filters.limit) : filteredLogs;
      setAuditLogs(result);
      return result;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [profile, auditLogs]);

  const exportAuditLogs = useCallback(async (
    filters: Parameters<typeof fetchAuditLogs>[0] = {}
  ) => {
    const logs = await fetchAuditLogs(filters);
    
    const csvContent = [
      // CSV Header
      'Timestamp,User ID,Action,Resource Type,Resource ID,Success,IP Address,Error Message',
      // CSV Data
      ...logs.map(log => [
        log.timestamp,
        log.user_id,
        log.action,
        log.resource_type,
        log.resource_id || '',
        log.success ? 'true' : 'false',
        log.ip_address || '',
        log.error_message || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [fetchAuditLogs]);

  const clearLocalLogs = useCallback(() => {
    setAuditLogs([]);
  }, []);

  return {
    auditLogs,
    loading,
    logSpreadsheetAction,
    logAdminAction,
    logAuthAction,
    logDataAccess,
    createAuditEvent,
    fetchAuditLogs,
    exportAuditLogs,
    clearLocalLogs
  };
}